import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import ShieldRoundedIcon           from '@mui/icons-material/ShieldRounded'
import SettingsRoundedIcon         from '@mui/icons-material/SettingsRounded'
import AccessTimeRoundedIcon       from '@mui/icons-material/AccessTimeRounded'
import LocalFireDepartmentRounded  from '@mui/icons-material/LocalFireDepartmentRounded'
import WaterDropRoundedIcon        from '@mui/icons-material/WaterDropRounded'
import HelpOutlineRoundedIcon      from '@mui/icons-material/HelpOutlineRounded'
import LaunchRoundedIcon           from '@mui/icons-material/LaunchRounded'
import HomeWorkRoundedIcon         from '@mui/icons-material/HomeWorkRounded'
import CheckRoundedIcon            from '@mui/icons-material/CheckRounded'
import { ink, line, surface, radius, shadow, font, bp, transition, cardStyle } from 'features/dashboard/tokens'
import ProgrammeBoard from 'features/compliance/ProgrammeBoard'
import ComplianceSetupProgress from 'features/compliance/ComplianceSetupProgress'
import ComplianceScoreExplainer from 'features/compliance/ComplianceScoreExplainer'
import { criticalityLabel, criticalityTint } from 'features/compliance/obligationConstants'
import { scoreColour } from 'features/dashboard/charts/scoreColour'
import StaticMap from 'components/StaticMap'
import BuildingOwnerField from 'components/BuildingOwnerField'
import { logActions } from 'utils/constants'
import { DASHBOARD, remedialsViewUrl } from 'features/actions/workspace/remedialsQuery'
import { RemedialCountsExplainer } from 'features/actions/workspace/Explainers'

const ACCENT = '#D24724'

const DOMAIN_ICONS = {
  shield:   ShieldRoundedIcon,
  settings: SettingsRoundedIcon,
  clock:    AccessTimeRoundedIcon,
  alert:    LocalFireDepartmentRounded,
  water:    WaterDropRoundedIcon,
}

const formatPct = v => v === null || v === undefined ? '—' : `${Math.round(v)}%`

/**
 * BP View — focused on a single Building Passport. Shows the BP identity,
 * its overall score, one card per Task Domain (click → drawer), and a
 * "Drill into units" CTA when there are units linked.
 */
const BpView = ({ bpName, bpMeta = {}, score, kpis = {}, domains = [], missingObligations = [], hasUnits, onDrillDomain, onDrillUnits, bpId, trail = [], onTrailJump, drillTarget, onDrillDown, opsInsights = null, openObligations = 0, onToggleGapDismissed }) => {
  const ancestors = trail.slice(0, -1)
  const navigate = useNavigate()
  const [busyGap, setBusyGap] = useState(null)

  const toggleGapDismissed = async gap => {
    if (!onToggleGapDismissed || busyGap) return
    setBusyGap(gap.slug)
    try {
      await onToggleGapDismissed(gap)
    } finally {
      setBusyGap(null)
    }
  }

  const outstandingGaps = missingObligations.filter(g => !g.isDismissed).length
  const parentPortfolio = trail.find(seg => seg.type === 'portfolio')
  const parentEstate = trail.find(seg => seg.type === 'estate')
  return (
    <Grid>
      <Main>
        <Hero>
          <HeroBody>
            <Trail>
              {ancestors.map((seg, idx) => (
                <React.Fragment key={`${seg.type}-${seg.id}`}>
                  <TrailChip onClick={() => onTrailJump?.(seg)}>{seg.label}</TrailChip>
                  <TrailSep>›</TrailSep>
                </React.Fragment>
              ))}
              {drillTarget && onDrillDown && (
                <DrillPill onClick={onDrillDown} title={`See all ${drillTarget.label} on this Building Passport`}>
                  Drill into {drillTarget.label} · {drillTarget.count} →
                </DrillPill>
              )}
            </Trail>
            <IdentityEyebrow>Building Passport</IdentityEyebrow>
            <IdentityName>{bpName}</IdentityName>
            {bpMeta?.address && <IdentityMeta>{bpMeta.address}</IdentityMeta>}

            {bpId && (
              <HeroLinks>
                <HeroLink $strong onClick={() => navigate(`/bp-list/${bpId}`)}>Manage Building Passport ↗</HeroLink>
                <HeroLink onClick={() => navigate(`/bp-list/${bpId}`)}>Files &amp; documents ↗</HeroLink>
                {parentEstate && (
                  <HeroLink onClick={() => navigate(`/estates/${parentEstate.id}`)}>Manage estate ↗</HeroLink>
                )}
                {parentPortfolio && (
                  <HeroLink onClick={() => navigate(`/portfolios/${parentPortfolio.id}`)}>Manage portfolio ↗</HeroLink>
                )}
              </HeroLinks>
            )}

            <HeroStatRow>
              <HeroStat>
                <HeroStatValue>{formatPct(score)}</HeroStatValue>
                <HeroStatLabel>compliance</HeroStatLabel>
                <HeroExplainer>
                  <ComplianceScoreExplainer align='right' />
                </HeroExplainer>
              </HeroStat>
              <HeroStatDivider />
              <HeroStat>
                <HeroStatValue $tint={(kpis.overdue || 0) > 0 ? '#FFD8C8' : '#fff'}>{kpis.overdue ?? 0}</HeroStatValue>
                <HeroStatLabel>overdue</HeroStatLabel>
              </HeroStat>
              <HeroStatDivider />
              <HeroStat onClick={hasUnits ? onDrillUnits : undefined} $clickable={hasUnits}>
                <HeroStatValue>{kpis.units ?? 0}</HeroStatValue>
                <HeroStatLabel>Unit Passports{hasUnits ? ' ↗' : ''}</HeroStatLabel>
              </HeroStat>
            </HeroStatRow>

          </HeroBody>

          <HeroDonut>
            <Donut size={140} stroke={13} score={score} />
          </HeroDonut>
        </Hero>

      {bpId && <ComplianceSetupProgress bpId={bpId} />}

      <PreviewRow>
        <PreviewCard $accent='#33becc' onClick={() => navigate(`/bp-list/${bpId}/compliance`)}>
          <PreviewEyebrow>Overview</PreviewEyebrow>
          <PreviewTitle>Compliance overview</PreviewTitle>
          <PreviewKpiRow>
            <PreviewKpiBig>{opsInsights?.tasks?.total ?? 0}</PreviewKpiBig>
            <PreviewKpiHint>tasks · {opsInsights?.tasks?.last30d?.completed ?? 0} completed 30d</PreviewKpiHint>
          </PreviewKpiRow>
          <PreviewCta>Open overview →</PreviewCta>
        </PreviewCard>
        <PreviewCard $accent='#D24724' onClick={() => navigate(`/bp-list/${bpId}/compliance/programme`)}>
          <PreviewEyebrow>Compliance tracker</PreviewEyebrow>
          <PreviewTitle>{formatPct(score)} · {openObligations} to action</PreviewTitle>
          <PreviewKpiRow>
            <PreviewKpiBig>{domains.length}</PreviewKpiBig>
            <PreviewKpiHint>domains scored</PreviewKpiHint>
          </PreviewKpiRow>
          <PreviewCta>Open tracker →</PreviewCta>
        </PreviewCard>
        <PreviewCard $accent='#d97706' onClick={() => navigate(`/bp-list/${bpId}/compliance/setup`)}>
          <PreviewEyebrow>Obligation list</PreviewEyebrow>
          <PreviewTitle>Review what applies</PreviewTitle>
          <PreviewKpiRow>
            <PreviewKpiBig>{openObligations}</PreviewKpiBig>
            <PreviewKpiHint>obligations to review</PreviewKpiHint>
          </PreviewKpiRow>
          <PreviewCta>Open obligation list →</PreviewCta>
        </PreviewCard>
        <PreviewCard $accent='#7C5FE0' onClick={() => navigate(`/bp-list/${bpId}/compliance/assets`)}>
          <PreviewEyebrow>Asset register</PreviewEyebrow>
          <PreviewTitle>What this building contains</PreviewTitle>
          <PreviewKpiRow>
            <PreviewKpiBig>{bpMeta?.assetsCount ?? '—'}</PreviewKpiBig>
            <PreviewKpiHint>assets · AI can detect more</PreviewKpiHint>
          </PreviewKpiRow>
          <PreviewCta>Open register →</PreviewCta>
        </PreviewCard>
        <PreviewCard $accent='#b00020' onClick={() => navigate(remedialsViewUrl({ bpId }, DASHBOARD))}>
          <PreviewEyebrow>Remedials</PreviewEyebrow>
          <PreviewTitle>{opsInsights?.actions?.views?.overdue ?? 0} overdue · {opsInsights?.actions?.views?.needsDecision ?? 0} need a decision</PreviewTitle>
          <PreviewKpiRow>
            <PreviewKpiBig>{opsInsights?.actions?.views?.allOpen ?? 0}</PreviewKpiBig>
            <PreviewKpiHint>open now</PreviewKpiHint>
          </PreviewKpiRow>
          <PreviewCta>Open remedials →</PreviewCta>
        </PreviewCard>
      </PreviewRow>

      {bpId && (
        <TrackerMount>
          <ProgrammeBoard bpId={bpId} embedded />
        </TrackerMount>
      )}

      <Section>
        <SectionHead>
          <SectionEyebrow>Task domains</SectionEyebrow>
          <SectionHint>Click any to see the breakdown</SectionHint>
        </SectionHead>
        {domains.length === 0 ? (
          <Empty>
            No task domains scored for this Building Passport yet. Upload evidence or schedule tasks to start tracking.
          </Empty>
        ) : (
          <DomainGrid>
            {domains.map(d => {
              const Icon = DOMAIN_ICONS[d.icon] || HelpOutlineRoundedIcon
              const tint = d.colour || scoreColour(d.score)
              const overdue = d.overdue || 0
              return (
                <DomainCard
                  key={d.id}
                  onClick={() => onDrillDomain?.(d)}
                  $tint={tint}
                  role='button'
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onDrillDomain?.(d) }}
                >
                  <DomainCardHead>
                    <DomainCardIconChip $tint={tint}><Icon style={{ fontSize: 22 }} /></DomainCardIconChip>
                    {overdue > 0 && <DomainOverdueBadge>{overdue}</DomainOverdueBadge>}
                  </DomainCardHead>
                  <DomainCardName>{d.name}</DomainCardName>
                  <DomainCardScore $tint={scoreColour(d.score)}>{formatPct(d.score)}</DomainCardScore>
                  <DomainProgress>
                    <DomainProgressFill
                      style={{
                        width: `${Math.max(2, d.score ?? 0)}%`,
                        background: tint,
                      }}
                    />
                  </DomainProgress>
                </DomainCard>
              )
            })}
          </DomainGrid>
        )}
      </Section>

      {missingObligations.length > 0 && (
        <GapSection>
          <SectionHead>
            <SectionEyebrow $accent>
              Missing obligations · {outstandingGaps}
              {outstandingGaps !== missingObligations.length && ` of ${missingObligations.length}`}
            </SectionEyebrow>
            <SectionHint>Evidence not yet on file. Upload to clear, or tick off to dismiss.</SectionHint>
          </SectionHead>
          <GapList>
            {missingObligations.slice(0, 6).map(gap => {
              const isDismissed = !!gap.isDismissed
              return (
                <GapRow key={gap.slug} $dismissed={isDismissed}>
                  <GapTick
                    $on={isDismissed}
                    disabled={busyGap === gap.slug}
                    title={isDismissed ? 'Restore this item' : 'Dismiss. Keep in list, ticked off'}
                    onClick={() => toggleGapDismissed(gap)}>
                    {isDismissed && <CheckRoundedIcon style={{ fontSize: 13, color: '#fff' }} />}
                  </GapTick>
                  <GapCrit $colour={criticalityTint(gap.criticalityWeight)}>{criticalityLabel(gap.criticalityWeight)}</GapCrit>
                  <GapBody>
                    <GapName $dismissed={isDismissed}>{gap.name}</GapName>
                    <GapMeta>
                      {gap.regulatoryFramework && <span>{gap.regulatoryFramework}</span>}
                      {gap.obligationKind && <span>{gap.obligationKind}</span>}
                      {isDismissed && <GapDismissedNote>dismissed</GapDismissedNote>}
                    </GapMeta>
                  </GapBody>
                  <GapArrow>→</GapArrow>
                </GapRow>
              )
            })}
          </GapList>
          {missingObligations.length > 6 && (
            <GapFoot>+ {missingObligations.length - 6} more queued</GapFoot>
          )}
        </GapSection>
      )}

      {(() => {
        const ts = bpMeta.taskSummary || {}
        const as = bpMeta.actionsSummary || {}
        const taskTotal   = (ts.open || 0) + (ts.overdue || 0) + (ts.inProgress || 0) + (ts.completed_30d || ts.completed30d || 0)
        const actionTotal = (as.open || 0) + (as.pendingReview || 0) + (as.resolved_30d || as.resolved30d || 0)
        if (taskTotal === 0 && actionTotal === 0) return null
        return (
          <OpsRow>
            <OpsCard>
              <OpsHead>
                <OpsHeadTitle>Tasks</OpsHeadTitle>
                <OpsHeadLink onClick={() => navigate(`/bp-list/${bpId}/taskReports`)}>Open ↗</OpsHeadLink>
              </OpsHead>
              {taskTotal === 0 ? (
                <Empty>No tasks tracked yet on this Building Passport.</Empty>
              ) : (
                <OpsStatGrid>
                  <OpsStat>
                    <OpsStatValue>{ts.open ?? 0}</OpsStatValue>
                    <OpsStatLabel>Open</OpsStatLabel>
                  </OpsStat>
                  <OpsStat $tint={(ts.overdue || 0) > 0 ? '#b00020' : null}>
                    <OpsStatValue>{ts.overdue ?? 0}</OpsStatValue>
                    <OpsStatLabel>Overdue</OpsStatLabel>
                  </OpsStat>
                  <OpsStat>
                    <OpsStatValue>{ts.inProgress ?? 0}</OpsStatValue>
                    <OpsStatLabel>In progress</OpsStatLabel>
                  </OpsStat>
                  <OpsStat>
                    <OpsStatValue>{ts.completed_30d ?? ts.completed30d ?? 0}</OpsStatValue>
                    <OpsStatLabel>Done · 30d</OpsStatLabel>
                  </OpsStat>
                </OpsStatGrid>
              )}
            </OpsCard>

            <OpsCard>
              <OpsHead>
                <OpsHeadTitle>Remedials</OpsHeadTitle>
                <RemedialCountsExplainer />
                <OpsHeadLink onClick={() => navigate(remedialsViewUrl({ bpId }, DASHBOARD))}>Open ↗</OpsHeadLink>
              </OpsHead>
              {actionTotal === 0 ? (
                <Empty>No remedials raised on this Building Passport.</Empty>
              ) : (
                <OpsStatGrid>
                  {[
                    { key: 'open', label: 'Open', value: as.open ?? 0, view: 'all_open' },
                    { key: 'overdue', label: 'Overdue', value: as.overdue ?? 0, view: 'overdue', tint: (as.overdue || 0) > 0 ? '#b00020' : null },
                    { key: 'needsDecision', label: 'Needs a decision', value: as.needsDecision ?? 0, view: 'needs_decision', tint: (as.needsDecision || 0) > 0 ? '#c2410c' : null },
                    { key: 'pendingReview', label: 'Awaiting review', value: as.pendingReview ?? 0, view: 'awaiting_review' },
                  ].map(stat => (
                    <OpsStat
                      key={stat.key}
                      as='button'
                      type='button'
                      $tint={stat.tint}
                      onClick={() => navigate(remedialsViewUrl({ bpId }, stat.view))}
                      aria-label={`${stat.label}: ${stat.value}. Open the list`}
                      style={{ cursor: 'pointer', minHeight: 40, textAlign: 'left' }}
                    >
                      <OpsStatValue>{stat.value}</OpsStatValue>
                      <OpsStatLabel>{stat.label}</OpsStatLabel>
                    </OpsStat>
                  ))}
                </OpsStatGrid>
              )}
            </OpsCard>
          </OpsRow>
        )
      })()}

      {(bpMeta.openIncidents || []).length > 0 && (
        <IncidentsSection>
          <SectionHead>
            <SectionEyebrow>Open incidents</SectionEyebrow>
            <OpsHeadLink onClick={() => navigate(`/bp-list/${bpId}/incidents`)}>
              Open incident log ↗
            </OpsHeadLink>
          </SectionHead>
          <IncidentList>
            {bpMeta.openIncidents.map(i => (
              <IncidentRow key={i.id} onClick={() => navigate(`/incidents/${i.id}`)}>
                <SeverityChip $tone={severityTint(i.severity)}>{i.severity || 'unrated'}</SeverityChip>
                <IncidentBody>
                  <IncidentNarrative>{i.narrative || i.category}</IncidentNarrative>
                  <IncidentMeta>
                    {i.category && <span>{i.category}</span>}
                    {i.occurredAt && <span>{relativeTime(i.occurredAt)}</span>}
                  </IncidentMeta>
                </IncidentBody>
                <IncidentArrow>→</IncidentArrow>
              </IncidentRow>
            ))}
          </IncidentList>
        </IncidentsSection>
      )}

      </Main>

      <SideRail>
        <SideRailCard>
          <SideRailHead>Building info</SideRailHead>
          <Map
            latitude={bpMeta.latitude}
            longitude={bpMeta.longitude}
            postcode={bpMeta.postalCode}
            address={bpMeta.address}
          />
          {bpMeta.address && (
            <SideRow>
              <SideLabel>Address</SideLabel>
              <SideValue>{bpMeta.address}</SideValue>
            </SideRow>
          )}
          <BuildingOwnerField bpId={bpId} value={bpMeta.buildingOwner} canEdit={!!bpMeta.canEdit} />
          {bpMeta.uprn && (
            <SideRow>
              <SideLabel>UPRN</SideLabel>
              <SideValue $mono>{bpMeta.uprn}</SideValue>
            </SideRow>
          )}
          {bpMeta.primaryType && (
            <SideRow>
              <SideLabel>Type</SideLabel>
              <SideValue>{bpMeta.primaryType}</SideValue>
            </SideRow>
          )}
          {bpMeta.storeys !== undefined && bpMeta.storeys !== null && (
            <SideRow>
              <SideLabel>Storeys</SideLabel>
              <SideValue>
                {bpMeta.storeys}
                {bpMeta.underground > 0 && <SideSub> + {bpMeta.underground} below</SideSub>}
              </SideValue>
            </SideRow>
          )}
          {bpMeta.heightMetres > 0 && (
            <SideRow>
              <SideLabel>Height</SideLabel>
              <SideValue>{bpMeta.heightMetres.toFixed(1)} m <SideSub>(derived)</SideSub></SideValue>
            </SideRow>
          )}
          {bpMeta.unitsCount !== undefined && bpMeta.unitsCount !== null && (
            <SideRow>
              <SideLabel>Units</SideLabel>
              <SideValue>{bpMeta.unitsCount}</SideValue>
            </SideRow>
          )}
          {bpMeta.classification && (
            <SideRow>
              <SideLabel>Classification</SideLabel>
              <SideValue>{bpMeta.classification}</SideValue>
            </SideRow>
          )}
          {bpMeta.occupied !== null && bpMeta.occupied !== undefined && (
            <SideRow>
              <SideLabel>Occupied</SideLabel>
              <SideValue>{bpMeta.occupied ? 'Yes' : 'No'}</SideValue>
            </SideRow>
          )}
          {bpMeta.status && (
            <SideRow>
              <SideLabel>Status</SideLabel>
              <StatusChip $tone={bpMeta.status === 'active' ? '#33becc' : ink.mute}>
                {bpMeta.status}
              </StatusChip>
            </SideRow>
          )}
        </SideRailCard>

        {(bpMeta.asbestos || bpMeta.claddingIssue || bpMeta.floodRisk) && (
          <SideRailCard>
            <SideRailHead>Risk flags</SideRailHead>
            {bpMeta.asbestos && <RiskRow>⚠️ Asbestos present</RiskRow>}
            {bpMeta.claddingIssue && <RiskRow>⚠️ Cladding issue flagged</RiskRow>}
            {bpMeta.floodRisk && <RiskRow>⚠️ Flood risk</RiskRow>}
          </SideRailCard>
        )}

        {Array.isArray(bpMeta.recentActivity) && bpMeta.recentActivity.length > 0 && (
          <SideRailCard>
            <SideRailHead>Recent activity</SideRailHead>
            {bpMeta.recentActivity.map(a => (
              <ActivityRow key={a.id}>
                <ActivityIcon $tone={a.tone}>{iconGlyph(a.icon)}</ActivityIcon>
                <ActivityBody>
                  <ActivityAction>{a.summary || prettyAction(a.action)}</ActivityAction>
                  <ActivityMeta>
                    {a.user} · {relativeTime(a.createdAt)}
                  </ActivityMeta>
                </ActivityBody>
              </ActivityRow>
            ))}
          </SideRailCard>
        )}

        {bpMeta.people && bpMeta.people.length > 0 && (
          <SideRailCard>
            <SideRailHead>Accountable people</SideRailHead>
            {bpMeta.people.map((p, i) => (
              <PersonRow key={`${p.email}-${i}`}>
                <PersonAvatar>{p.name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'}</PersonAvatar>
                <PersonInfo>
                  <PersonName>{p.name}</PersonName>
                  <PersonRole>{p.role}</PersonRole>
                </PersonInfo>
              </PersonRow>
            ))}
          </SideRailCard>
        )}
      </SideRail>
    </Grid>
  )
}

const iconGlyph = icon => {
  switch (icon) {
    case 'file':     return '📄'
    case 'note':     return '📝'
    case 'incident': return '⚠'
    case 'action':   return '✓'
    case 'user':     return '👤'
    case 'unit':     return '🏠'
    case 'edit':     return '✎'
    case 'share':    return '↗'
    default:         return '·'
  }
}

const toneTint = tone => {
  switch (tone) {
    case 'positive': return '#15803d'
    case 'warn':     return '#d97706'
    case 'risk':     return '#b00020'
    default:         return ink.mute
  }
}

const severityTint = sev => {
  switch ((sev || '').toLowerCase()) {
    case 'critical':               return '#b00020'
    case 'high':                   return '#c2410c'
    case 'medium':                 return '#d97706'
    case 'low':                    return '#15803d'
    default:                       return ink.mute
  }
}

const prettyAction = key => {
  if (!key) return ''
  const known = logActions.find(l => l.key === key)?.value
  if (known) return known.charAt(0).toUpperCase() + known.slice(1)
  const parts = key.replace(/^building_passport\./, '').replace(/^action\./, 'remedial.').split('.')
  const verb = (parts.pop() || '').replace(/_/g, ' ')
  const text = `${verb} ${parts.join(' ').replace(/_/g, ' ')}`.trim()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const relativeTime = iso => {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const s = Math.max(0, Math.floor(ms / 1000))
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(mo / 12)}y ago`
}

const Map = ({ latitude, longitude, postcode, address }) => {
  const [resolved, setResolved] = useState(
    latitude && longitude ? { lat: Number(latitude), lng: Number(longitude) } : null,
  )

  useEffect(() => {
    if (resolved) return
    const code = (postcode || '').replace(/\s+/g, '')
    if (!code) return
    let cancelled = false
    fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(code)}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (cancelled || !j?.result) return
        setResolved({ lat: j.result.latitude, lng: j.result.longitude })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [postcode, resolved])

  if (!resolved) {
    return (
      <MapPlaceholder>
        <MapPlaceholderText>{address || 'No coordinates on file'}</MapPlaceholderText>
      </MapPlaceholder>
    )
  }

  const { lat, lng } = resolved
  const q = `${lat},${lng}`
  const externalUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`

  return (
    <MapCanvas>
      <StaticMap lat={lat} lng={lng} height="100%" label={address || 'Building location'} />
      <MapLink href={externalUrl} target='_blank' rel='noreferrer'>Open in Google Maps ↗</MapLink>
    </MapCanvas>
  )
}

export default BpView

const Donut = ({ size = 140, stroke = 14, score }) => {
  const r = (size - stroke) / 2
  const C = 2 * Math.PI * r
  const hasScore = score !== null && score !== undefined
  const safe = hasScore ? Math.max(0, Math.min(100, score)) : 0
  const filled = (safe / 100) * C
  return (
    <DonutWrap style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} fill='none'
          stroke='rgba(255,255,255,0.25)' strokeWidth={stroke} />
        {hasScore && (
          <circle cx={size/2} cy={size/2} r={r} fill='none'
            stroke='#fff' strokeWidth={stroke}
            strokeDasharray={`${filled} ${C}`} strokeLinecap='round'
            transform={`rotate(-90 ${size/2} ${size/2})`}
            style={{ transition: 'stroke-dasharray 600ms cubic-bezier(0.22, 0.61, 0.36, 1)' }} />
        )}
      </svg>
      <DonutCenter>
        {hasScore ? formatPct(score) : <DonutEmpty>No score yet</DonutEmpty>}
      </DonutCenter>
    </DonutWrap>
  )
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Hero = styled.section`
  position: relative;
  padding: 24px 28px;
  background: ${ACCENT};
  color: #fff;
  border-radius: ${radius.md};
  box-shadow: ${shadow.lift};
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 28px;
  align-items: center;
  margin-bottom: 18px;
  animation: ${fadeUp} 320ms ease both;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: transparent;
    pointer-events: none;
  }
`

const HeroBody = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  min-width: 0;
  position: relative;
`

const HeroDonut = styled.div`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
`

const HeroStatRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 18px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(255,255,255,0.22);
`

const HeroStat = styled.div`
  display: flex; flex-direction: column; gap: 2px;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  &:hover {
    ${({ $clickable }) => $clickable ? 'opacity: 0.85;' : ''}
  }
`

const HeroStatValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.6px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ $tint }) => $tint || '#fff'};
`

const HeroStatLabel = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.75);
  font-weight: 800;
`

const HeroStatDivider = styled.span`
  width: 1px;
  background: rgba(255,255,255,0.22);
`

const HeroExplainer = styled.div`
  margin-top: 6px;
`

const HeroLinks = styled.div`
  display: flex; gap: 14px; margin-top: 14px;
  flex-wrap: wrap;
`

const PreviewRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin: 16px 0;
  @media (max-width: 1320px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  @media (max-width: ${bp.tablet}) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: ${bp.mobile})  { grid-template-columns: 1fr; }
`

const TrackerMount = styled.div`
  margin-bottom: 16px;
`

const PreviewCard = styled.button`
  ${cardStyle}
  font: inherit;
  position: relative;
  text-align: left;
  padding: 20px 22px 20px 26px;
  display: flex; flex-direction: column; gap: 6px;
  cursor: pointer;
  overflow: hidden;
  transition: ${transition};
  &::before {
    content: '';
    position: absolute;
    top: 0; bottom: 0; left: 0;
    width: 4px;
    background: ${({ $accent }) => $accent};
  }
  &:hover {
    transform: translateY(-2px);
    border-color: ${({ $accent }) => `${$accent}66`};
    box-shadow: ${shadow.lift};
  }
`

const PreviewEyebrow = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const PreviewTitle = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.heading};
  letter-spacing: -0.3px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.2;
`

const PreviewKpiRow = styled.div`
  display: inline-flex; align-items: baseline; gap: 8px;
  margin-top: 4px;
`

const PreviewKpiBig = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

const PreviewKpiHint = styled.span`
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const PreviewCta = styled.span`
  margin-top: 6px;
  font-size: ${font.small};
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
`

const HeroLink = styled.button`
  font: inherit;
  background: none;
  border: none;
  padding: 0;
  color: #fff;
  font-size: ${font.small};
  font-weight: ${({ $strong }) => $strong ? 800 : 700};
  letter-spacing: 0.04em;
  text-decoration: underline;
  text-decoration-thickness: ${({ $strong }) => $strong ? '2px' : '1px'};
  text-underline-offset: 4px;
  cursor: pointer;
  opacity: ${({ $strong }) => $strong ? 1 : 0.92};
  &:hover { opacity: 1; }
`

const Trail = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`

const TrailChip = styled.button`
  font: inherit;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.28);
  color: #fff;
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: ${radius.pill};
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.32); }
`

const TrailSep = styled.span`
  color: rgba(255,255,255,0.55);
  font-size: ${font.small};
  font-weight: 700;
`

const DrillPill = styled.button`
  font: inherit;
  margin-left: 6px;
  display: inline-flex; align-items: center; gap: 6px;
  background: #33becc;
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff;
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: ${radius.pill};
  cursor: pointer;
  box-shadow: ${shadow.lift};
  transition: ${transition};
  &:hover { transform: translateY(-1px); filter: brightness(1.08); }
`

const GapSection = styled.section`
  ${cardStyle}
  padding: 18px 22px;
  margin-top: 16px;
`

const GapList = styled.div` display: flex; flex-direction: column; `

const GapRow = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 10px 8px;
  border-bottom: 1px solid ${surface.wash};
  border-radius: ${radius.sm};
  opacity: ${({ $dismissed }) => $dismissed ? 0.55 : 1};
  transition: opacity 180ms ease;
  &:last-child { border-bottom: none; }
  &:hover { background: ${surface.wash}; }
`

const GapTick = styled.button`
  font: inherit;
  width: 20px; height: 20px;
  border-radius: 50%;
  border: 1.5px solid ${({ $on }) => $on ? '#2aa6b3' : '#cbcbd2'};
  background: ${({ $on }) => $on ? '#2aa6b3' : 'transparent'};
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: all 160ms ease;
  &:hover { border-color: #2aa6b3; }
`

const GapDismissedNote = styled.span`
  font-style: italic;
  color: #2aa6b3;
`

const GapCrit = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 9px;
  border-radius: ${radius.pill};
  background: ${({ $colour }) => `${$colour}1A`};
  color: ${({ $colour }) => $colour};
  border: 1px solid ${({ $colour }) => `${$colour}33`};
`

const GapBody = styled.div` display: flex; flex-direction: column; min-width: 0; `

const GapName = styled.span`
  font-size: ${font.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  text-decoration: ${({ $dismissed }) => $dismissed ? 'line-through' : 'none'};
`

const GapMeta = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  display: flex; gap: 10px;
`

const GapArrow = styled.span`
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const GapFoot = styled.div`
  margin-top: 10px;
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  text-align: center;
  font-weight: 700;
`

const OpsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 16px;
  @media (max-width: ${bp.mobile}) { grid-template-columns: 1fr; }
`

const OpsCard = styled.section`
  ${cardStyle}
  padding: 16px 18px;
`

const OpsHead = styled.div`
  display: flex; justify-content: space-between; align-items: baseline;
  margin-bottom: 12px;
`

const OpsHeadTitle = styled.h3`
  margin: 0;
  font-size: ${font.body};
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
`

const OpsHeadLink = styled.button`
  font: inherit;
  background: none;
  border: none;
  padding: 0;
  font-size: ${font.caption};
  font-weight: 700;
  color: ${ACCENT};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const OpsStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`

const OpsStat = styled.div`
  padding: 10px 8px;
  background: ${({ $tint, theme }) => $tint ? `${$tint}1A` : theme.colors.lighterGrey};
  border: 1px solid ${({ $tint }) => $tint ? `${$tint}33` : 'transparent'};
  border-radius: ${radius.md};
  display: flex; flex-direction: column; gap: 2px; align-items: flex-start;
`

const OpsStatValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1;
`

const OpsStatLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const IncidentsSection = styled.section`
  ${cardStyle}
  padding: 18px 22px;
  margin-top: 16px;
`

const IncidentList = styled.div` display: flex; flex-direction: column; `

const IncidentRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 10px 8px;
  border-bottom: 1px solid ${surface.wash};
  cursor: pointer;
  border-radius: ${radius.sm};
  &:last-child { border-bottom: none; }
  &:hover { background: ${surface.wash}; }
`

const SeverityChip = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 9px;
  border-radius: ${radius.pill};
  background: ${({ $tone }) => `${$tone}1A`};
  color: ${({ $tone }) => $tone};
  border: 1px solid ${({ $tone }) => `${$tone}33`};
`

const IncidentBody = styled.div` display: flex; flex-direction: column; min-width: 0; `

const IncidentNarrative = styled.span`
  font-size: ${font.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`

const IncidentMeta = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  display: flex; gap: 10px;
`

const IncidentArrow = styled.span`
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const ActivityRow = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid ${surface.wash};
  &:last-child { border-bottom: none; }
`

const ActivityIcon = styled.span`
  width: 28px; height: 28px;
  border-radius: ${radius.round};
  display: inline-flex; align-items: center; justify-content: center;
  background: ${({ $tone }) => `${toneTint($tone)}1A`};
  color: ${({ $tone }) => toneTint($tone)};
  font-size: ${font.body};
  line-height: 1;
`

const ActivityBody = styled.div` display: flex; flex-direction: column; min-width: 0; `

const ActivityAction = styled.span`
  font-size: ${font.small};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const ActivityMeta = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  @media (max-width: ${bp.tablet}) { grid-template-columns: 1fr; }
`

const Main = styled.div` min-width: 0; `

const SideRail = styled.aside`
  display: flex; flex-direction: column; gap: 14px;
`

const SideRailCard = styled.section`
  ${cardStyle}
  padding: 16px 18px;
`

const SideRailHead = styled.h3`
  margin: 0 0 12px;
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const MapCanvas = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: ${radius.md};
  overflow: hidden;
  margin-bottom: 12px;
  border: 1px solid ${line.base};
`

const MapFrame = styled.iframe`
  display: block;
  width: 100%;
  height: calc(100% + 48px);
  border: 0;
  transform: translateY(-48px);
`

const MapPin = styled.div`
  position: absolute;
  left: 50%; top: 50%;
  width: 30px; height: 30px;
  transform: translate(-50%, -100%);
  border-radius: 50% 50% 50% 0;
  background: #33becc;
  border: 3px solid #fff;
  box-shadow: ${shadow.lift};
  pointer-events: none;
  rotate: -45deg;
`

const PinDot = styled.span`
  position: absolute;
  left: 50%; top: 50%;
  width: 8px; height: 8px;
  border-radius: ${radius.round};
  background: #fff;
  transform: translate(-50%, -50%);
`

const MapLink = styled.a`
  position: absolute;
  bottom: 8px; right: 8px;
  font-size: ${font.caption};
  font-weight: 700;
  padding: 4px 9px;
  border-radius: ${radius.sm};
  background: rgba(255,255,255,0.95);
  color: ${({ theme }) => theme.colors.haiti};
  text-decoration: none;
  border: 1px solid ${line.base};
  box-shadow: ${shadow.card};
  &:hover { background: #fff; color: #fd836b; }
`

const MapPlaceholder = styled.div`
  width: 100%;
  height: 120px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 12px;
  padding: 12px;
`

const MapPlaceholderText = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  text-align: center;
`

const SideRow = styled.div`
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid ${surface.wash};
  &:last-child { border-bottom: none; }
`

const SideLabel = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
`

const SideValue = styled.span`
  font-size: ${font.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  text-align: right;
  font-family: ${({ $mono }) => $mono ? 'JetBrains Mono, ui-monospace, monospace' : 'inherit'};
  max-width: 60%;
`

const SideSub = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 500;
  margin-left: 4px;
`

const StatusChip = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: ${radius.pill};
  background: ${({ $tone }) => `${$tone}1A`};
  color: ${({ $tone }) => $tone};
  border: 1px solid ${({ $tone }) => `${$tone}33`};
`

const RiskRow = styled.div`
  font-size: ${font.small};
  color: #b00020;
  font-weight: 600;
  padding: 4px 0;
`

const PersonRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid ${surface.wash};
  &:last-child { border-bottom: none; }
`

const PersonAvatar = styled.div`
  width: 32px; height: 32px;
  border-radius: ${radius.round};
  display: inline-flex; align-items: center; justify-content: center;
  background: ${ACCENT};
  color: #fff;
  font-size: ${font.caption};
  font-weight: 800;
`

const PersonInfo = styled.div`
  display: flex; flex-direction: column; min-width: 0;
`

const PersonName = styled.span`
  font-size: ${font.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const PersonRole = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
  @media (max-width: ${bp.tablet}) { grid-template-columns: 1fr 1fr; }
`

const IdentityCard = styled.section`
  position: relative;
  padding: 22px 24px;
  background: ${ACCENT};
  color: #fff;
  border-radius: ${radius.md};
  overflow: hidden;
  min-height: 180px;
  display: flex; flex-direction: column; gap: 6px;
  box-shadow: ${shadow.lift};
  animation: ${fadeUp} 320ms ease both;
`

const IdentityEyebrow = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.78);
  font-weight: 800;
`

const IdentityName = styled.h2`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.8px;
  line-height: 1.1;
`

const IdentityMeta = styled.span`
  font-size: ${font.body};
  color: rgba(255,255,255,0.78);
`

const IdentityScoreRow = styled.div`
  margin-top: auto;
  display: inline-flex; align-items: baseline; gap: 8px;
`

const IdentityScore = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.hero};
  letter-spacing: -1.6px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

const IdentityScoreLabel = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.78);
  font-weight: 800;
`

const DonutWrap = styled.div`
  display: inline-flex; align-items: center; justify-content: center;
  position: relative;
`

const DonutCenter = styled.span`
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  display: flex; align-items: center; justify-content: center;
  text-align: center;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
  color: #fff;
`

const DonutEmpty = styled.span`
  font-family: system-ui, sans-serif;
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.78);
  text-align: center;
  line-height: 1.2;
  max-width: 80px;
`

const KpiTile = styled.div`
  ${cardStyle}
  display: flex; flex-direction: column; gap: 4px;
  padding: 18px 20px;
  min-height: 180px;
`

const KpiLabel = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const KpiValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.hero};
  letter-spacing: -1.2px;
  color: ${({ $tint, theme }) => $tint || theme.colors.haiti};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

const KpiHint = styled.span`
  margin-top: auto;
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const KpiCta = styled.button`
  font: inherit;
  margin-top: auto;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: ${font.small};
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  text-align: left;
  &:hover { text-decoration: underline; }
`

const Section = styled.section`
  ${cardStyle}
  padding: 18px 22px;
  margin-bottom: 16px;
`

const SectionHead = styled.header`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
`

const SectionEyebrow = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const SectionHint = styled.span`
  font-size: ${font.caption};
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid ${line.base};
  border-radius: ${radius.pill};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Empty = styled.div`
  padding: 32px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
`

const DomainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`

const DomainCard = styled.div`
  ${cardStyle}
  position: relative;
  padding: 16px 16px 14px;
  cursor: pointer;
  transition: ${transition};
  &:hover {
    transform: translateY(-2px);
    border-color: ${({ $tint }) => $tint};
    box-shadow: ${shadow.lift};
  }
`

const DomainCardHead = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
`

const DomainCardIconChip = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px;
  border-radius: ${radius.round};
  background: ${({ $tint }) => `${$tint}1A`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
`

const DomainOverdueBadge = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: ${radius.pill};
  background: rgba(204, 34, 0, 0.10);
  color: #b00020;
  border: 1px solid rgba(204, 34, 0, 0.24);
`

const DomainCardName = styled.div`
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.25;
  min-height: 32px;
`

const DomainCardScore = styled.div`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.6px;
  color: ${({ $tint }) => $tint};
  margin: 8px 0 6px;
  font-variant-numeric: tabular-nums;
`

const DomainProgress = styled.div`
  height: 6px;
  background: ${surface.wash};
  border-radius: ${radius.xs};
  overflow: hidden;
`

const DomainProgressFill = styled.div`
  height: 100%;
  border-radius: ${radius.xs};
  transition: width 480ms ease;
`

const ActionRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px;
  margin-bottom: 16px;
`

const ActionPill = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  background: ${({ $primary, theme }) => $primary ? theme.colors.haiti : 'transparent'};
  color: ${({ $primary, theme }) => $primary ? '#fff' : theme.colors.haiti};
  border: 1px solid ${({ $primary, theme }) => $primary ? theme.colors.haiti : line.base};
  border-radius: ${radius.pill};
  font-size: ${font.small}; font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  &:hover {
    background: ${({ $primary, theme }) => $primary ? theme.colors.haiti : theme.colors.lighterGrey};
    filter: ${({ $primary }) => $primary ? 'brightness(1.08)' : 'none'};
    transform: translateY(-1px);
  }
`
